import { DEFAULT_BRUSH_FALLOFF } from './brushFalloff';

/**
 * Millimetres to scene units.
 *
 * The socket world is centimetre-scale (`scaleModelToCentimeters`), matching
 * the `MM_TO_UNIT` used by the flare and ML-reduction tools.
 */
export const MM_TO_SCENE = 0.1;

/**
 * The contract between the tool card (millimetres and percentages, which is
 * what a clinician reasons about) and the sculpt kernels (scene units and
 * per-dab magnitudes, which is what the maths needs).
 *
 * Keeping the conversion in one named place — rather than scattering `* 0.1`
 * through a React component — is what stops the two vocabularies leaking into
 * each other, and makes the unit choices reviewable.
 */

/**
 * Which family of kernels a `SculptSession` should run for a stroke.
 *
 * Every brush shares the session's machinery — BVH query, stroke sampling,
 * partial normals, per-stroke undo — and differs only in the arithmetic it
 * applies to the vertices under the cursor. This is the field that selects it,
 * stamped onto the settings object by the `to*Settings` function at each tool's
 * UI boundary so a handler never has to name a kernel.
 *
 * Distinct from {@link BRUSH_MODE}, which is the inflate brush's *direction*.
 *
 * @typedef {'inflateDeflate' | 'smooth'} SculptBrushFamily
 */
export const SCULPT_BRUSH = {
  INFLATE_DEFLATE: 'inflateDeflate',
  SMOOTH: 'smooth',
};

/** @typedef {'inflate' | 'deflate'} BrushMode */

export const BRUSH_MODE = {
  INFLATE: 'inflate',
  DEFLATE: 'deflate',
};

/**
 * UI-facing defaults and slider bounds, in millimetres / percent.
 *
 * The ranges are sized for prosthetic socket rectification: a 12 mm brush is
 * about a fingertip on the socket wall, and 1 mm of relief is already a
 * clinically meaningful change.
 */
export const BRUSH_LIMITS = {
  size: { min: 2, max: 60, step: 1, default: 12 },        // mm, radius
  intensity: { min: 1, max: 100, step: 1, default: 35 },  // %
  maxDisplacement: { min: 0.1, max: 10, step: 0.1, default: 2 }, // mm per stroke
  smoothing: { min: 0, max: 100, step: 5, default: 25 },  // %
};

/**
 * How much of the per-stroke displacement limit a single full-strength dab
 * delivers at 100% intensity.
 *
 * With the default 15% spacing a stroke lays down roughly seven dabs per brush
 * width, so this value sets how quickly a stroke reaches its clamp: high enough
 * that the tool feels responsive, low enough that a single twitch cannot jump
 * straight to the limit.
 */
const DAB_FRACTION_AT_FULL_INTENSITY = 0.34;

/**
 * @typedef {Object} BrushSettings
 * @property {SculptBrushFamily} brushType
 * @property {BrushMode} mode
 * @property {number} radius          - brush radius, scene units (world space)
 * @property {number} amountPerDab    - unsigned displacement per dab, scene units
 * @property {number} maxDisplacement - stroke displacement clamp, scene units
 * @property {number} smoothing       - relax strength, 0..1
 * @property {string} falloff         - a {@link BRUSH_FALLOFF} id
 * @property {boolean} pinBoundary    - leave open-boundary (trimline) vertices alone
 * @property {Uint8Array | null} [mask] - optional per-vertex protection, 0..255
 */

/**
 * Convert the tool card's state into kernel-ready {@link BrushSettings}.
 *
 * @param {Object} ui
 * @param {BrushMode} ui.mode
 * @param {number} ui.size             - brush radius in mm
 * @param {number} ui.intensity        - 1..100
 * @param {number} ui.maxDisplacement  - mm of travel allowed per stroke
 * @param {number} ui.smoothing        - 0..100
 * @param {string} [ui.falloff]
 * @param {boolean} [ui.pinBoundary=true]
 * @param {Uint8Array | null} [ui.mask]
 * @param {boolean} [ui.invert=false]  - flips inflate <-> deflate (Ctrl-drag)
 * @returns {BrushSettings}
 */
export function toBrushSettings({
  mode = BRUSH_MODE.INFLATE,
  size = BRUSH_LIMITS.size.default,
  intensity = BRUSH_LIMITS.intensity.default,
  maxDisplacement = BRUSH_LIMITS.maxDisplacement.default,
  smoothing = BRUSH_LIMITS.smoothing.default,
  falloff = DEFAULT_BRUSH_FALLOFF,
  pinBoundary = true,
  mask = null,
  invert = false,
}) {
  const normalizedIntensity = clamp01(intensity / 100);
  const limitScene = Math.max(maxDisplacement, 0) * MM_TO_SCENE;

  const effectiveMode = invert
    ? mode === BRUSH_MODE.INFLATE
      ? BRUSH_MODE.DEFLATE
      : BRUSH_MODE.INFLATE
    : mode;

  return {
    brushType: SCULPT_BRUSH.INFLATE_DEFLATE,
    mode: effectiveMode,
    radius: Math.max(size, 0.01) * MM_TO_SCENE,
    amountPerDab: limitScene * DAB_FRACTION_AT_FULL_INTENSITY * normalizedIntensity,
    maxDisplacement: limitScene,
    smoothing: clamp01(smoothing / 100),
    // Unknown ids are tolerated: `getFalloffFn` falls back to the default curve.
    falloff: falloff || DEFAULT_BRUSH_FALLOFF,
    pinBoundary,
    mask,
  };
}

/**
 * Clamp to [0, 1], treating a missing or NaN value as 0.
 *
 * Shared with the other `to*Settings` converters: every one of them turns a
 * percentage slider into a unit factor, and a stray NaN reaching a kernel
 * silently blackens a patch of the mesh rather than throwing.
 *
 * @param {number} value
 * @returns {number}
 */
export function clamp01(value) {
  if (!Number.isFinite(value)) return 0;
  return value < 0 ? 0 : value > 1 ? 1 : value;
}
